import { TextField } from "@mui/material";
import * as React from "react";

import { useVizDispatch } from "@/shared/state/visualization";
import { renameThreeDEntity } from "@/shared/state/visualization/actions";
import type {
  Entity,
  EntityRegistry,
} from "@/shared/state/visualization/schema/v1/three-d";

import styles from "./EntityConfig.module.css";

interface EntityNameFieldProps {
  entity: Entity;
  registry: EntityRegistry;
  panelId: string;
}

/**
 * Renames an entity.
 *
 * **Commits on blur or Enter, never per keystroke.** An id is a key, not a
 * value: renaming rewrites the entity's record key, every child's `parent`, and
 * the camera target. Dispatching mid-typing would fire that cascade for every
 * intermediate string, most of which are empty or collide with a name the user is
 * halfway through typing.
 *
 * Escape reverts to the committed name, which is the only way back once the
 * field has been edited.
 */
export function EntityNameField({
  entity,
  registry,
  panelId,
}: EntityNameFieldProps) {
  const dispatch = useVizDispatch();
  const [draft, setDraft] = React.useState(entity.id);

  // Re-sync when the id changes underneath us — an undo, or another client's
  // edit to a shared workspace.
  React.useEffect(
    function adoptCommittedName() {
      setDraft(entity.id);
    },
    [entity.id],
  );

  const trimmed = draft.trim();
  const error = React.useMemo(() => {
    if (trimmed === entity.id) {
      return undefined;
    }
    if (trimmed === "") {
      return "Name is required";
    }
    if (registry[trimmed]) {
      return "Name is already taken";
    }
    return undefined;
  }, [trimmed, entity.id, registry]);

  const commit = React.useCallback(() => {
    if (error !== undefined) {
      return;
    }
    if (trimmed === entity.id) {
      // Normalize away whitespace the user typed but did not otherwise change.
      setDraft(entity.id);
      return;
    }
    dispatch(renameThreeDEntity(panelId, entity.id, trimmed));
  }, [dispatch, error, panelId, entity.id, trimmed]);

  return (
    <TextField
      label="Name"
      size="small"
      margin="dense"
      fullWidth
      autoComplete="off"
      value={draft}
      error={error !== undefined}
      helperText={error}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          commit();
        } else if (event.key === "Escape") {
          setDraft(entity.id);
        }
      }}
      className={styles.input}
      InputLabelProps={{ className: styles.input }}
      InputProps={{ className: styles.input }}
    />
  );
}
